const getQueryValue = (value: any): string => {
  const raw = Array.isArray(value) ? value[0] : value;
  return typeof raw === 'string' ? raw.trim() : '';
};

const JUMPVIEW_CONTROL_QUERY_KEYS = ['jumpview', 'orgid', 't'];

const isControlQueryKey = (key: string): boolean => {
  return JUMPVIEW_CONTROL_QUERY_KEYS.includes(key.toLowerCase());
};

const getForwardQuery = (query: any): Record<string, any> => {
  if (!query || typeof query !== 'object') return {};

  return Object.keys(query).reduce((result: Record<string, any>, key) => {
    if (isControlQueryKey(key)) return result;
    const value = query[key];
    if (value === undefined || value === null || value === '') return result;
    result[key] = value;
    return result;
  }, {});
};

const parseQueryString = (queryString: string): Record<string, any> => {
  const params = new URLSearchParams(queryString);
  const result: Record<string, any> = {};

  params.forEach((value, key) => {
    if (result[key] === undefined) {
      result[key] = value;
    } else if (Array.isArray(result[key])) {
      result[key].push(value);
    } else {
      result[key] = [result[key], value];
    }
  });

  return result;
};

const safeDecode = (value: string): string => {
  let result = value;
  for (let i = 0; i < 2; i += 1) {
    try {
      const decoded = decodeURIComponent(result);
      if (decoded === result) break;
      result = decoded;
    } catch {
      break;
    }
  }
  return result.trim();
};

const parseJumpviewValue = (value: string): { target: string; query: Record<string, any> } => {
  const decoded = safeDecode(value);
  if (!decoded) return { target: '', query: {} };

  const questionIndex = decoded.indexOf('?');
  if (questionIndex >= 0) {
    return {
      target: decoded.slice(0, questionIndex),
      query: parseQueryString(decoded.slice(questionIndex + 1)),
    };
  }

  const ampIndex = decoded.indexOf('&');
  if (ampIndex > 0 && !decoded.startsWith('/')) {
    return {
      target: decoded.slice(0, ampIndex),
      query: parseQueryString(decoded.slice(ampIndex + 1)),
    };
  }

  return { target: decoded, query: {} };
};

const stripOriginRoute = (value: string): string => {
  try {
    const url = new URL(value, window.location.origin);
    if (url.hash.startsWith('#/')) {
      return url.hash.slice(1);
    }
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return `${url.pathname}${url.search || ''}`;
    }
  } catch {
    // 非完整 URL 时继续按普通路由参数处理
  }
  return value;
};

export const formatJumpviewPath = (value: string): string => {
  let target = parseJumpviewValue(value).target;
  if (!target) return '';

  target = stripOriginRoute(target);
  target = target.replace(/^#/, '');
  target = target.replace(/^\/?#\//, '/');

  if (!target || target === '/' || target.startsWith('?')) return '';
  if (target.startsWith('/')) return target;

  return `/${target}`;
};

export const getJumpviewRaw = (query: any): string => {
  if (!query || typeof query !== 'object') return '';

  const matchedKey = Object.keys(query).find((queryKey) => queryKey.toLowerCase() === 'jumpview');
  return getQueryValue(matchedKey ? query[matchedKey] : undefined);
};

export const getJumpviewRedirect = (query: any): string => {
  return formatJumpviewPath(getJumpviewRaw(query));
};

export const getJumpviewRouteLocation = (router: any, query: any): any => {
  const raw = getJumpviewRaw(query);
  if (!raw) return '';

  const parsedJumpview = parseJumpviewValue(raw);
  const forwardQuery = { ...getForwardQuery(query), ...parsedJumpview.query };
  const target = parsedJumpview.target;

  if (router.hasRoute?.(target)) {
    console.log('[Jumpview] 使用name跳转:', { name: target, query: forwardQuery });
    return {
      name: target,
      ...(Object.keys(forwardQuery).length ? { query: forwardQuery } : {}),
    };
  }

  const path = formatJumpviewPath(target);
  if (!path) return '';

  const resolved = router.resolve(path);
  const matchedName = resolved?.matched
    ?.slice()
    ?.reverse()
    ?.find((record: any) => record?.name)
    ?.name;
  const routeName = matchedName || resolved?.name;

  if (routeName) {
    console.log('[Jumpview] 使用name跳转:', {
      path,
      name: routeName,
      query: { ...forwardQuery, ...resolved.query },
      matched: resolved.matched?.map((record: any) => record?.name || record?.path),
    });
    return {
      name: routeName,
      query: { ...forwardQuery, ...resolved.query },
      hash: resolved.hash,
    };
  }

  console.warn('[Jumpview] 未匹配到路由name，回退path跳转:', path);
  return {
    path: resolved?.path || path,
    query: { ...forwardQuery, ...resolved?.query },
    hash: resolved?.hash,
  };
};
